import React, { lazy, Suspense, useMemo, useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { Layout, Menu, Spin, Grid, Drawer, Alert, Button } from 'antd';
import {
  LayoutDashboard,
  Target,
  Settings,
  Users,
  Building2,
  ClipboardList,
  PlusCircle,
  Bell,
  FileDown,
  Wallet,
  TrendingDown,
  Sparkles,
  Search,
  LineChart,
  RefreshCw,
  DollarSign,
  Zap,
  Menu as MenuIcon,
  MessageSquare,
  UsersRound,
  Briefcase,
} from 'lucide-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { msalConfig } from './config/msal-config';
import { persistOptions, queryClient } from './config/query-persistence';
import { DELIVERY_TEAMS_ROUTE } from './config/wfm-routes';
import {
  loadWfmLandingPage, loadWfmLayerPage, loadWfmTeamPage, preloadWfmChunksForPath,
} from './config/wfm-page-loaders';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { MonthProvider, useMonth } from './contexts/MonthContext';
import { DataRangeProvider, useDataRange } from './contexts/DataRangeContext';
import useAuthStore, { selectPortalTier, selectIsAdmin, selectIsSuperAdmin, selectHasDirectReports, selectIsLeader, selectHasAiSpendPilot } from './stores/auth-store';
import { trackNavClick, trackVirtualPageView, trackThemeToggle, setUserProperties } from './services/analytics-service';
import { EnterpriseDrillProvider, useEnterpriseDrillContext } from './contexts/EnterpriseDrillContext';
import { useRoutePrefetch } from './hooks/use-route-prefetch';
import useWfmAccess from './hooks/use-wfm-access';
import AccessGate, { shouldRenderLocalGate } from './components/AccessGate';
import UserModeSignOutPrompt from './components/user-mode-sign-out-prompt';
import BreadcrumbBar from './components/navigation/breadcrumb-bar';
import ProfileChip from './components/navigation/profile-chip';

import PageSkeleton from './components/PageSkeleton';
import coxLogo from './assets/cox-automotive-logo.svg';
import './App.css';

const msalInstance = new PublicClientApplication(msalConfig);

const EmailVerification = lazy(() => import('./pages/EmailVerification'));
const AdminUserManagement = lazy(() => import('./pages/AdminUserManagement'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const MyDashboardPage = lazy(() => import('./pages/my-dashboard-page'));
const EnterprisePage = lazy(() => import('./pages/enterprise-page'));
const DirectReportsPage = lazy(() => import('./pages/direct-reports-page'));
const DirectReportsIndividualPage = lazy(() => import('./pages/direct-reports-individual-page'));
const PersonSearchPage = lazy(() => import('./pages/person-search-page'));
const MyOutcomesPage = lazy(() => import('./pages/my-outcomes-page'));
const EnterpriseOutcomesPage = lazy(() => import('./pages/enterprise-outcomes-page'));
const OrgOutcomesPage = lazy(() => import('./pages/org-outcomes-page'));
const CreateOutcomePage = lazy(() => import('./pages/create-outcome-page'));
const OutcomeDetailPage = lazy(() => import('./pages/outcome-detail-page'));
const CheckinFormPage = lazy(() => import('./pages/checkin-form-page'));
const ApprovalReviewPage = lazy(() => import('./pages/approval-review-page'));
const CheckinReviewPage = lazy(() => import('./pages/checkin-review-page'));
const AlertsThresholdsPage = lazy(() => import('./pages/alerts-thresholds-page'));
const ExportReportsPage = lazy(() => import('./pages/export-reports-page'));
const ManageBudgetPage = lazy(() => import('./pages/manage-budget-page'));
const ManageAiSpendPage = lazy(() => import('./pages/manage-ai-spend-page'));
const TokenForecastPage = lazy(() => import('./pages/token-forecast-page'));
const CostOptimizationPage = lazy(() => import('./pages/cost-optimization-page'));
const AdoptionTrendsPage = lazy(() => import('./pages/adoption-trends-page'));
const UserProfilePage = lazy(() => import('./pages/user-profile-page'));
const SyncHealthPage = lazy(() => import('./pages/sync-health-page'));
const CostConfigurationPage = lazy(() => import('./pages/cost-configuration-page'));
const WfmLandingPage = lazy(loadWfmLandingPage);
const WfmLayerPage = lazy(loadWfmLayerPage);
const WfmTeamPage = lazy(loadWfmTeamPage);
preloadWfmChunksForPath(window.location.pathname);

const { Content, Sider } = Layout;
const { useBreakpoint } = Grid;

const icon = (Icon) => <Icon size={16} strokeWidth={2} />;

const allMenuItems = [
  { type: 'group', label: 'DASHBOARDS', key: 'g-dashboards' },
  { key: 'my-dashboard', icon: icon(LayoutDashboard), label: 'My Dashboard', path: '/my-dashboard', tier: 'user' },
  { key: 'enterprise', icon: icon(Building2), label: 'Enterprise', path: '/enterprise', tier: 'leader', hidden: true },
  { key: 'delivery-teams', icon: icon(Briefcase), label: 'Delivery Teams', path: DELIVERY_TEAMS_ROUTE, tier: 'user', hidden: true },
  { key: 'direct-reports', icon: icon(Users), label: 'Direct Reports', path: '/direct-reports', tier: 'leader', hidden: true },
  { key: 'person-search', icon: icon(Search), label: 'Person Search', path: '/person-search', tier: 'leader'},

  { type: 'group', label: 'OUTCOMES', key: 'g-outcomes' },
  { key: 'my-outcomes', icon: icon(Target), label: 'My Outcomes', path: '/my-outcomes', tier: 'superadmin', hidden: true },
  { key: 'manager-review', icon: icon(ClipboardList), label: 'Manager Review', path: '/manager-review', tier: 'superadmin', hidden: true },
  { key: 'create-outcome', icon: icon(PlusCircle), label: 'Create Outcome', path: '/create-outcome', tier: 'superadmin', hidden: true },

  { type: 'group', label: 'ACTIONS', key: 'g-actions' },
  { key: 'manage-ai-spend', icon: icon(Wallet), label: 'Manage AI Spend', path: '/manage-ai-spend', tier: 'user', hidden: true },
  { key: 'manage-budget', icon: icon(DollarSign), label: 'Manage Budget', path: '/manage-budget', tier: 'superadmin', hidden: true },

  { type: 'group', label: 'ANALYSIS', key: 'g-analysis' },
  { key: 'cost-optimization', icon: icon(DollarSign), label: 'Cost Optimization', path: '/cost-optimization', tier: 'superadmin', hidden: true },
  { key: 'adoption-trends', icon: icon(TrendingDown), label: 'Adoption Trends', path: '/adoption-trends', tier: 'superadmin', hidden: true },
  { key: 'model-efficiency', icon: icon(Zap), label: 'Model Efficiency', path: '/token-forecast', tier: 'superadmin', hidden: true },

  { type: 'group', label: 'SETTINGS', key: 'g-settings' },
  { key: 'alerts', icon: icon(Bell), label: 'Alerts & Thresholds', path: '/alerts', tier: 'superadmin', hidden: true },
  { key: 'export-reports', icon: icon(FileDown), label: 'Export & Reports', path: '/export-reports', tier: 'superadmin', hidden: true },
  { key: 'admin-users', icon: icon(Settings), label: 'User Management', path: '/admin/users', tier: 'admin', hidden: true },
  { key: 'cost-configuration', icon: icon(Wallet), label: 'Cost Configuration', path: '/admin/cost-configuration', tier: 'admin', hidden: true },
  { key: 'sync-health', icon: icon(RefreshCw), label: 'Sync Health', path: '/admin/sync-health', tier: 'superadmin', hidden: true },
];

const TIER_ACCESS = { user: ['user'], leader: ['user', 'leader'], admin: ['user', 'leader', 'admin'], superadmin: ['user', 'leader', 'admin', 'superadmin'] };

function TopBar({ onMenuClick }) {
  const { themes, currentTheme, changeTheme } = useTheme();
  const isDark = themes[currentTheme]?.mode === 'dark';
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const toggle = () => {
    const lightKeys = Object.entries(themes).filter(([, t]) => t.mode === 'light').map(([k]) => k);
    const darkKeys = Object.entries(themes).filter(([, t]) => t.mode === 'dark').map(([k]) => k);
    const newMode = isDark ? 'light' : 'dark';
    trackThemeToggle(newMode);
    changeTheme(isDark ? lightKeys[0] : darkKeys[0]);
  };

  return (
    <div style={{
      height: 50,
      background: '#001219',
      display: 'flex',
      alignItems: 'center',
      padding: '0 16px',
      gap: 10,
      flexShrink: 0,
      minWidth: 0,
    }}>
      {isMobile && (
        <button
          onClick={onMenuClick}
          aria-label="Open navigation menu"
          style={{ background: 'none', border: 'none', color: '#FFFFFF', cursor: 'pointer', padding: '4px', flexShrink: 0, display: 'flex', alignItems: 'center' }}
        >
          <MenuIcon size={20} />
        </button>
      )}
      <img src={coxLogo} alt="Cox Automotive" style={{ height: 22, flexShrink: 0, filter: 'brightness(0) invert(1)' }} />
      {!isMobile && <span style={{ width: 1, height: 22, background: '#E9D8A644', flexShrink: 0 }} />}
      {!isMobile && <span style={{ color: '#FFFFFF', fontSize: 21, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: 1 }}>Next-Gen Platform Hub</span>}
      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        <span style={{ fontSize: 14, color: '#E9D8A6AA' }}>{isDark ? 'Dark' : 'Light'}</span>
        <button
          onClick={toggle}
          aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          style={{
            position: 'relative',
            width: 45,
            height: 25,
            borderRadius: 12,
            border: '1px solid #CA6702',
            background: isDark ? '#003544' : '#C5BDAE',
            cursor: 'pointer',
            padding: 0,
            transition: 'all 0.3s ease',
            flexShrink: 0,
          }}
        >
          <div style={{
            position: 'absolute',
            top: 2,
            left: isDark ? 18 : 2,
            width: 18,
            height: 18,
            borderRadius: '50%',
            background: isDark ? '#EE9B00' : '#BB3E03',
            transition: 'left 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 10,
            boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
          }}>
            {isDark ? '🌙' : '☀️'}
          </div>
        </button>
      </div>
    </div>
  );
}

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useAuthStore(s => s.logout);
  const userEmail = useAuthStore(s => s.userEmail);
  const userProfile = useAuthStore(s => s.userProfile);
  const isAdmin = useAuthStore(selectIsAdmin);
  const isSuperAdmin = useAuthStore(selectIsSuperAdmin);
  const isLeader = useAuthStore(selectIsLeader);
  const hasDirectReports = useAuthStore(selectHasDirectReports);
  const hasAiSpendPilot = useAuthStore(selectHasAiSpendPilot);
  const authLoading = useAuthStore(s => s.isLoading);
  const portalTier = useAuthStore(selectPortalTier);

  useEffect(() => {
    trackVirtualPageView(location.pathname, document.title);
  }, [location.pathname]);

  const { periodParam } = useMonth();
  const { prefetch, cancel } = useRoutePrefetch(periodParam);

  const allowedTiers = TIER_ACCESS[portalTier] || TIER_ACCESS.user;

  const {
    canAccess: canAccessDeliveryTeams,
    isResolved: deliveryTeamsAccessResolved,
    isError: deliveryTeamsAccessFailed,
    refetch: retryDeliveryTeamsAccess,
  } = useWfmAccess();
  const deliveryTeamsRoute = (page) => {
    if (deliveryTeamsAccessFailed) {
      return (
        <Alert
          type="error" showIcon style={{ margin: 24 }}
          message="Could not check your access to Delivery Teams."
          action={<Button size="small" onClick={() => retryDeliveryTeamsAccess()}>Retry</Button>}
        />
      );
    }
    if (!deliveryTeamsAccessResolved || canAccessDeliveryTeams) return page;
    return <Navigate to="/my-dashboard" replace />;
  };

  const menuItems = useMemo(() => {
    const visible = allMenuItems.filter(item => {
      if (item.type === 'group') return true;
      if (item.hidden) {
        if (item.key === 'delivery-teams') return canAccessDeliveryTeams;
        if (item.key === 'direct-reports') return hasDirectReports;
        if (item.key === 'enterprise') return hasDirectReports;
        if (item.key === 'manage-ai-spend') return hasAiSpendPilot && hasDirectReports;
        if (isSuperAdmin) return true;
        if (isAdmin && item.tier === 'admin') return true;
        return false;
      }
      return true;
    });
    return visible
      .filter((item, idx, arr) => {
        if (item.type === 'group') {
          const next = arr[idx + 1];
          return next && next.type !== 'group';
        }
        return true;
      })
      .map(item => {
        if (item.type === 'group') return item;
        const accessible = item.key === 'enterprise'
          ? hasDirectReports
          : allowedTiers.includes(item.tier);
        const labelNode = item.label;
        return {
          ...item,
          label: labelNode,
          disabled: !accessible,
          style: accessible ? undefined : { pointerEvents: 'none' },
          onMouseEnter: () => accessible && item.path && prefetch(item.path),
          onMouseLeave: () => cancel(),
        };
      });
  }, [allowedTiers, isSuperAdmin, isAdmin, hasDirectReports, hasAiSpendPilot, canAccessDeliveryTeams, prefetch, cancel]);

  const { resetToTop: resetEnterpriseDrill } = useEnterpriseDrillContext();

  const handleMenuClick = ({ key, domEvent }) => {
    trackNavClick(key, portalTier);
    const item = allMenuItems.find(i => i.key === key);
    if (item?.path) {
      if (domEvent.ctrlKey || domEvent.metaKey || domEvent.button === 1) {
        window.open(item.path, '_blank');
        domEvent.preventDefault();
      } else {
        if (key === 'enterprise') resetEnterpriseDrill();
        navigate(item.path);
      }
    }
  };

  const selectedKeys = useMemo(() => {
    const item = allMenuItems.find(i => i.path === location.pathname);
    if (item) return [item.key];
    if (location.pathname.startsWith('/outcome/')) return ['my-outcomes'];
    if (location.pathname.startsWith('/manager-review/')) return ['manager-review'];
    if (location.pathname.startsWith('/direct-reports/')) return ['direct-reports'];
    if (location.pathname.startsWith(DELIVERY_TEAMS_ROUTE)) return ['delivery-teams'];
    return ['my-dashboard'];
  }, [location.pathname]);

  const screens = useBreakpoint();
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = !screens.md;
  const isTablet = false;

  const sidebarContent = (
    <>
      <div className="sidebar-menu-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
        <Menu
          mode="inline"
          inlineCollapsed={false}
          selectedKeys={selectedKeys}
          style={{
            border: 'none',
            background: 'transparent',
            padding: '15px 0',
          }}
          onClick={(info) => { handleMenuClick(info); if (isMobile) setMobileOpen(false); }}
          items={menuItems}
        />
      </div>
      {!isTablet && <div style={{ height: 1, background: 'var(--theme-border, #D9D2C4)', margin: '0 15px', flexShrink: 0 }} />}
      {!isTablet && (
        <a
          href="https://slack.com/app_redirect?channel=ai-cost-dashboard"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 24px',
            fontSize: 13,
            color: 'var(--theme-textSecondary)',
            textDecoration: 'none',
          }}
        >
          <MessageSquare size={14} />
          Send Feedback
        </a>
      )}
      {!isTablet && <ProfileChip />}
    </>
  );

  return (
    <div style={{ height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <TopBar onMenuClick={() => setMobileOpen(true)} />
      <BreadcrumbBar />
      <Layout style={{ flex: 1, overflow: 'hidden' }}>
      {!isMobile && (
        <Sider
          trigger={null}
          collapsible={false}
          collapsed={isTablet}
          collapsedWidth={64}
          width={232}
          style={{
            height: '100%',
            overflow: 'hidden',
            background: 'var(--theme-cardBg, #FFFFFF)',
            borderRight: '1px solid var(--theme-border, #D9D2C4)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {sidebarContent}
        </Sider>
      )}
      {isMobile && (
        <Drawer
          placement="left"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          width={232}
          styles={{ body: { padding: 0, background: 'var(--theme-cardBg, #FFFFFF)', display: 'flex', flexDirection: 'column', height: '100%' }, header: { display: 'none' } }}
        >
          {sidebarContent}
        </Drawer>
      )}

      <Layout style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Content style={{ overflow: 'auto', flex: 1, minHeight: 0, background: 'var(--theme-contentBg)' }}>
          <Suspense fallback={<PageSkeleton />}>
            <Routes>
              <Route path="/" element={<Navigate to="/my-dashboard" replace />} />

              <Route path="/my-dashboard" element={<MyDashboardPage />} />
              <Route path="/enterprise" element={hasDirectReports ? <EnterprisePage /> : <Navigate to="/my-dashboard" replace />} />
              <Route path="/direct-reports" element={isLeader && hasDirectReports ? <DirectReportsPage /> : <Navigate to="/my-dashboard" replace />} />
              <Route path="/direct-reports/individual" element={isLeader && hasDirectReports ? <DirectReportsIndividualPage /> : <Navigate to="/my-dashboard" replace />} />
              <Route path="/person-search" element={isLeader ? <PersonSearchPage /> : <Navigate to="/my-dashboard" replace />} />
              <Route
                path={DELIVERY_TEAMS_ROUTE}
                element={deliveryTeamsRoute(<WfmLandingPage />)}
              />
              <Route
                path={`${DELIVERY_TEAMS_ROUTE}/nodes/:nodeId`}
                element={deliveryTeamsRoute(<WfmLayerPage />)}
              />
              <Route
                path={`${DELIVERY_TEAMS_ROUTE}/teams/:teamId`}
                element={deliveryTeamsRoute(<WfmTeamPage />)}
              />

              <Route path="/dashboard" element={<Dashboard />} />

              {isSuperAdmin && <Route path="/my-outcomes" element={<MyOutcomesPage />} />}
              {isSuperAdmin && <Route path="/manager-review" element={<EnterpriseOutcomesPage />} />}
              {isSuperAdmin && <Route path="/org-outcomes" element={<OrgOutcomesPage />} />}
              {isSuperAdmin && <Route path="/create-outcome" element={<CreateOutcomePage />} />}
              {isSuperAdmin && <Route path="/outcome/:id" element={<OutcomeDetailPage />} />}
              {isSuperAdmin && <Route path="/outcome/:id/checkin" element={<CheckinFormPage />} />}
              {isSuperAdmin && <Route path="/manager-review/approval/:id" element={<ApprovalReviewPage />} />}
              {isSuperAdmin && <Route path="/manager-review/checkin/:id" element={<CheckinReviewPage />} />}

              <Route
                path="/manage-ai-spend"
                element={authLoading ? <PageSkeleton /> : hasAiSpendPilot && hasDirectReports ? <ManageAiSpendPage /> : <Navigate to="/my-dashboard" replace />}
              />
              {isSuperAdmin && <Route path="/manage-budget" element={<ManageBudgetPage />} />}
              {isSuperAdmin && <Route path="/token-forecast" element={<TokenForecastPage />} />}
              {isSuperAdmin && <Route path="/cost-optimization" element={<CostOptimizationPage />} />}
              {isSuperAdmin && <Route path="/adoption-trends" element={<AdoptionTrendsPage />} />}
              <Route path="/user-profile/:email" element={<UserProfilePage />} />

              {isSuperAdmin && <Route path="/alerts" element={<AlertsThresholdsPage />} />}
              {isSuperAdmin && <Route path="/export-reports" element={<ExportReportsPage />} />}
              {isAdmin && <Route path="/admin/users" element={<AdminUserManagement />} />}
              {isAdmin && <Route path="/admin/cost-configuration" element={<CostConfigurationPage />} />}
              {isSuperAdmin && <Route path="/admin/sync-health" element={<SyncHealthPage />} />}

              <Route path="*" element={<Navigate to="/my-dashboard" replace />} />
            </Routes>
          </Suspense>
        </Content>
      </Layout>
    </Layout>
    </div>
  );
}

function AuthenticatedApp() {
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const isLoading = useAuthStore(s => s.isLoading);
  const location = useLocation();
  const navigate = useNavigate();
  const hasModeUser = new URLSearchParams(location.search).get('mode') === 'user';
  const onAllowedHost = shouldRenderLocalGate({
    hostname: window.location.hostname,
    search: location.search,
  });

  React.useEffect(() => {
    const hasSession = useAuthStore.getState().bootstrapOptimistic();

    msalInstance.initialize().then(() => {
      useAuthStore.getState().handleRedirectResponse(msalInstance)
        .then(() => {
          if (!useAuthStore.getState().isAuthenticated) {
            useAuthStore.setState({ isLoading: false });
          }
          useAuthStore.getState().bootstrapServerValidation();
        })
        .catch(() => {
          useAuthStore.setState({ isLoading: false });
        });
    }).catch(() => {
      if (!hasSession) {
        useAuthStore.setState({ isLoading: false });
      }
    });
  }, []);

  React.useEffect(() => {
    if (isAuthenticated) {
      const state = useAuthStore.getState();
      setUserProperties({
        portalTier: selectPortalTier(state),
        userRole: state.userRole,
        isAdmin: selectIsAdmin(state),
        isSuperAdmin: selectIsSuperAdmin(state),
      });
    }
  }, [isAuthenticated]);

  React.useEffect(() => {
    if (isAuthenticated && hasModeUser && onAllowedHost) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, hasModeUser, onAllowedHost, navigate]);

  if (isLoading) {
    return (
      <div style={{
        height: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'transparent',
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/verify-email" element={<EmailVerification />} />
        <Route path="*" element={<AccessGate />} />
      </Routes>
    );
  }

  if (hasModeUser && onAllowedHost) {
    return <UserModeSignOutPrompt />;
  }

  return (
    <EnterpriseDrillProvider>
      <AppContent />
    </EnterpriseDrillProvider>
  );
}

function MonthProviderWithDataRange({ children }) {
  const { latestDataMonth } = useDataRange();
  return (
    <MonthProvider initialMonth={latestDataMonth}>
      {children}
    </MonthProvider>
  );
}

function App() {
  return (
    <MsalProvider instance={msalInstance}>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
          <ThemeProvider>
            <DataRangeProvider>
              <MonthProviderWithDataRange>
                <AuthenticatedApp />
              </MonthProviderWithDataRange>
            </DataRangeProvider>
          </ThemeProvider>
        </PersistQueryClientProvider>
      </Router>
    </MsalProvider>
  );
}

export default App;
